import React, { Component } from 'react';
import { Redirect } from 'react-router-dom';
import {
  Typography, TextField, Button, MenuItem
} from '@material-ui/core';
import PropTypes from 'prop-types';

// Redux
import { connect } from 'react-redux';
import { setSuccess, setAlert, clearErrors } from '../redux/actions/alertActions';

// Components
import InfoButton from './InfoButton';

// Server
import { listBadgeTypes, listUnassignedStaff, createBadge, listUnassignedBadges } from '../api';

// Constants
import { DEFAULT } from '../constants';
import { T } from '../constants/tokens';
const SERIAL_NUMBER_HELP = 'The serial number of the badge that you would like to assign.';
const STAFF_MEMBER_HELP = 'The staff member that you would like to assign the badge to.';
const BADGE_TYPE_HELP = 'Administrator badges receive alerts for all school alerts. Nurse badges receive alerts for all school medical alerts. Normal badges do not receive school-wide alerts, except for red alerts.';
const DETECTED_HELP = 'Badges appear here once they report to the network: straight away when switched on, or within about 10 minutes if already on.';

class BadgeForm extends Component {
  constructor(props) {
    super(props);

    this.state = {
      unassignedBadges: [],
      serialNumber: '',
      assignedStaffMemberID: '',
      badgeTypeID: '',
      staffMembers: [],
      badgeTypes: [],
      formError: false,
      redirectToBadges: false,
      isLoading: false,
      isRefreshing: false,
    };
  }

  componentDidMount() {
    this.refreshBadges();

    listUnassignedStaff().then((staffMembers) => {
      this.setState({ staffMembers: staffMembers });
    }).catch(error => {
      this.props.setError(error ? error : 'Error getting staff members.');
    });

    listBadgeTypes().then(badgeTypes => {
      this.setState({ badgeTypes: badgeTypes }, () => {
        this.setState({ badgeType: badgeTypes?.length > 0 ? badgeTypes[0].getID() : '' });
      });
    });
  }

  // The server lists badges from the reports they send, without asking the
  // network, so this is cheap enough to run on open and on demand.
  refreshBadges = () => {
    this.setState({ isRefreshing: true });
    listUnassignedBadges().then((unassignedBadges) => {
      this.setState({ unassignedBadges: unassignedBadges, isRefreshing: false });
    }).catch(error => {
      this.setState({ isRefreshing: false });
      this.props.setError(error ? error : 'Error getting unassigned badges.');
    });
  }

  componentWillUnmount() {
    this.props.clearErrors();
  }

  onChange = (e) => {
    this.setState({ [e.target.name]: e.target.value });
  };

  hasFormError() {
    return this.state.serialNumber === '' || this.state.assignedStaffMemberID === '' || this.state.badgeTypeID === '';
  }

  submitForm = () => {
    // Ensure there is no form error
    const formError = this.hasFormError();
    this.setState({ formError });
    if (formError) return;

    this.setState({ isLoading: true });
    const { serialNumber, assignedStaffMemberID, badgeTypeID } = this.state;
    createBadge({ serialNumber, staffMemberID: assignedStaffMemberID, badgeTypeID }).then(() => {
      this.props.setSuccess('Successfully created new badge.');
      this.setState({ isLoading: false, serialNumber: '', assignedStaffMemberID: '', badgeTypeID: '' });
      this.props.onSuccess?.();
    }).catch(error => {
      this.setState({ isLoading: false });
      this.props.setError(error ? error : 'Error creating new badge.');
    });
  }

  render() {
    return this.state.redirectToBadges ? <Redirect to='/admin/badges' /> : (
      <form noValidate autoComplete='off' style={{ background: T.surfaceCard, border: '1px solid ' + T.hairline, borderRadius: 6, padding: 20, maxWidth: 680, width: '100%' }}>

        {/* Serial Number */}
        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 16, padding: '12px 0', borderBottom: '1px solid ' + T.hairlineSoft }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 4, flex: '1 1 200px', minWidth: 0 }}>
            <Typography variant='h6' style={{ flex: 1, minWidth: 0 }}>Serial Number</Typography>
            <InfoButton name={'Serial Number'} body={SERIAL_NUMBER_HELP} />
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8, flex: '1 1 280px', minWidth: 0 }}>
            <TextField
              required
              error={this.state.formError && this.state.serialNumber === ''}
              name="serialNumber"
              label="Serial Number"
              value={this.state.serialNumber}
              style={{ width: (this.props.isMobile ? '100%' : 'auto'), flex: (this.props.isMobile ? 'none' : '1') }}
              onChange={this.onChange}
              variant='outlined'
              disabled={this.state.isLoading} />
            <Button
              type="button"
              onClick={this.refreshBadges}
              variant="contained"
              style={{ marginLeft: (this.props.isMobile ? '0' : '10px'), marginTop: (this.props.isMobile ? '10px' : '0'), height: '50px' }}
              disabled={this.state.isRefreshing || this.state.isLoading}>
              {this.state.isRefreshing ? 'Refreshing...' : 'Refresh'}
            </Button>
          </div>
        </div>

        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 16, padding: '12px 0', borderBottom: '1px solid ' + T.hairlineSoft }}>
          <Typography variant='h6' style={{ flex: '1 1 200px', minWidth: 0 }}>Detected Badge</Typography>
          <TextField
            select
            label="Detected Badge"
            value={this.state.serialNumber}
            style={{ flex: '1 1 280px', minWidth: 0 }}
            onChange={this.onChange}
            name="serialNumber"
            variant='outlined'
            helperText={DETECTED_HELP}
            disabled={this.state.isLoading}>
            {this.state.unassignedBadges.length === 0 &&
              <MenuItem disabled value=''>No unassigned badges have reported yet</MenuItem>}
            {this.state.unassignedBadges.map((badge) => (
              <MenuItem key={badge.getSerialNumber()} value={badge.getSerialNumber()}>
                {badge.getSerialNumber()}
                {badge.getLastSeenTimestamp() && ' · seen ' + badge.getLastSeenTimestamp().toRelative()}
              </MenuItem>
            ))}
          </TextField>
        </div>

        {/* Assigned Staff Member */}
        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 16, padding: '12px 0', borderBottom: '1px solid ' + T.hairlineSoft }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 4, flex: '1 1 200px', minWidth: 0 }}>
            <Typography variant='h6' style={{ flex: 1, minWidth: 0 }}>Assigned Staff Member</Typography>
            <InfoButton name={'Staff Member'} body={STAFF_MEMBER_HELP} />
          </div>
          <TextField
            required
            select
            error={this.state.formError && this.state.assignedStaffMemberID === ''}
            name="assignedStaffMemberID"
            label="Assigned Staff Member"
            value={this.state.assignedStaffMemberID}
            style={{ flex: '1 1 280px', minWidth: 0 }}
            onChange={this.onChange}
            variant='outlined'
            disabled={this.state.isLoading}>
            {this.state.staffMembers.map((member) => (
              <MenuItem key={member.id} value={member.id}>
                {member.firstName} {member.lastName}
              </MenuItem>
            ))}
          </TextField>
        </div>

        {/* Badge Type */}
        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 16, padding: '12px 0', borderBottom: '1px solid ' + T.hairlineSoft }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 4, flex: '1 1 200px', minWidth: 0 }}>
            <Typography variant='h6' style={{ flex: 1, minWidth: 0 }}>Badge Type</Typography>
            <InfoButton name={'Badge Type'} body={BADGE_TYPE_HELP} />
          </div>
          <TextField
            required
            select
            error={this.state.formError && this.state.badgeTypeID === ''}
            name="badgeTypeID"
            label="Badge Type"
            value={this.state.badgeTypeID}
            style={{ flex: '1 1 280px', minWidth: 0 }}
            onChange={this.onChange}
            variant='outlined'
            disabled={this.state.isLoading}>
            {this.state.badgeTypes.map((type) => (
              <MenuItem key={type.id} value={type.id}>
                {type.name}
              </MenuItem>
            ))}
          </TextField>
        </div>

        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'flex-end', gap: 12, marginTop: 20 }}>
          <Button onClick={this.submitForm} variant="contained" color='primary' disabled={this.state.isLoading}>Save</Button>
        </div>
      </form>
    );
  }
}

const mapStateToProps = state => ({
  statusColor: state.status.schoolStatus ? state.status.schoolStatus.getColor() : DEFAULT,
  isFullWidth: state.ui.isFullWidth,
  isMobile: state.ui.isMobile
});

const mapDispatchToProps = dispatch => ({
  setError: (msg) => setAlert(dispatch, msg),
  setSuccess: (msg) => setSuccess(dispatch, msg),
  clearErrors: () => clearErrors()
});

export default connect(mapStateToProps, mapDispatchToProps)(BadgeForm);

BadgeForm.propTypes = {
  setSuccess: PropTypes.func,
  setError: PropTypes.func,
  clearErrors: PropTypes.func,
  isFullWidth: PropTypes.bool,
  statusColor: PropTypes.object,
  isMobile: PropTypes.bool.isRequired,
  onSuccess: PropTypes.func
};
